import axios from 'axios'
import Vue from 'vue'
import Vuex from 'vuex'
import VuexPersist from 'vuex-persist'

// import profile from '@cv/plugins/profile'
import vuexWebsocket from '@cv/plugins/vuex-socket'
import vuexSyncDocuments from '@cv/plugins/vuex-sync-documents'
import vuexCreatorVision from '@cv/plugins/vuex-creator-vision'
import account from '@cv/store/account'
import { AccountState } from '@cv/store/account.d'
import address from '@cv/store/address'
import agreement from '@cv/store/agreement'
import agreementDeliverable from '@cv/store/agreement-deliverable'
import agreementPaymentTerm from '@cv/store/agreement-payment-term'
import agreementShipment from '@cv/store/agreement-shipment'
import accountMediaTag from '@cv/store/account-media-tag'
import app from '@cv/store/app'
import appConfig from '@cv/store/app-config'
import { AppState } from '@cv/store/app.d'
import collab from '@cv/store/collab'
import collaboration from '@cv/store/collaboration'
import countries from '@cv/store/countries'
import media from '@cv/store/media'
import { MediaState } from '@cv/store/media.d'
import { SubscriberState } from '@cv/store/subscriber.d'
import payment from '@cv/store/payment'
import message from '@cv/store/message'
import note from '@cv/store/note'
import notification from '@cv/store/notification'
import objectEventSubscription from '@cv/store/object-event-subscription'
import plan from '@cv/store/plan'
import project from '@cv/store/project'
import service from '@cv/store/service'
import socialEntity from '@cv/store/social-entity'
import { SocialEntityState } from '@cv/store/social-entity.d'
import socket from '@cv/store/socket'
import subscriber from '@cv/store/subscriber'
import subscription from '@cv/store/subscription'
import systemEvent from '@cv/store/system-event'
import systemEventType from '@cv/store/system-event-type'
import taxDocument from '@cv/store/tax-document'
import user from '@cv/store/user'
import creatorVision from '@cv/store/creator-vision'
import spotlightSearch from '@cv/store/spotlight-search'

export interface RootState {
	app: AppState
	account: AccountState
	media: MediaState
	subscriber: SubscriberState
	socialEntity: SocialEntityState
}

axios.defaults.withCredentials = true

const modules = {
	app,
	appConfig,
	user,
	account,
	agreement,
	agreementDeliverable,
	agreementPaymentTerm,
	agreementShipment,
	socialEntity,
	collaboration,
	notification,
	systemEventType,
	project,
	plan,
	countries,
	service,
	media,
	subscriber,
	taxDocument,
	note,
	payment,
	collab,
	message,
	systemEvent,
	objectEventSubscription,
	address,
	subscription,
	socket,
	creatorVision,
	accountMediaTag,
	spotlightSearch
}

//   // ADD PERFORMANCE PROFILING
//   const addProfiling = (fn, key) => (...args) => profile(key)(fn(...args));
//   Object.keys(modules).forEach(name => {
//     ['getters', 'actions', 'mutations'].forEach(type => {
//       if(modules[name][type]){
//         Object.keys(modules[name][type]).forEach(method =>
//           modules[name][type][method] = addProfiling(
//             modules[name][type][method],
//             `${name}:${method}`
//           )
//         )
//       }
//     });
//   });

const selectedAccountStorage = new VuexPersist({
	key: 'vuex',
	storage: window.localStorage,
	modules: ['account', 'app', 'subscriber'],
	reducer: (state: RootState) => ({
		account: {
			selectedId: state.account.selectedId
		},
		app: {
			tours: state.app.tours
		},
		subscriber: {
			subscriber: state.subscriber.subscriber
		}
	}),
	filter: mutation =>
		[
			'account/SET_CREATOR_FILTER_SETTINGS',
			'account/SET_SELECTED_ACCOUNT',
			'app/SET_TOUR_STATUS',
			'subscriber/SET_SUBSCRIBER'
		].includes(mutation.type)
})

Vue.use(Vuex)
const store = new Vuex.Store({
	plugins: [selectedAccountStorage.plugin, vuexSyncDocuments, vuexWebsocket, vuexCreatorVision],
	modules
})

// Initialize spotlight search watchers
store.dispatch('spotlightSearch/init', store)

// =================
// When accounts are loaded
// Automatically select first one
// =================
store.watch(
	(state, getters) => getters['account/forUser'],
	accounts => {
		if (accounts.length > 0) {
			if (!store.state.account.selectedId) {
				// No account is currently selected.
				// Select the first account
				store.commit('account/SET_SELECTED_ACCOUNT', accounts[0]._id)
			} else {
				// An account is selected.
				// Confirm the account exists
				const accountExists = accounts.find(account => {
					return account._id === store.state.account.selectedId
				})
				if (!accountExists) {
					// Account doesn't exist
					// Select the first account they have access to
					store.commit('account/SET_SELECTED_ACCOUNT', accounts[0]._id)
				}
			}
		} else {
			// This user has no accounts
			// They probably haven't loaded yet
			// store.commit('account/SET_SELECTED_ACCOUNT', null)
		}
	}
)
// Commit all module/ON_DEAUTHENTICATE mutations
// when user de-authenticates
store.watch(
	(state, getters) => getters['user/isAuthenticated'],
	isAuthenticated => {
		if (isAuthenticated) {
			console.log('User Authenticated!')
			// User authenticated.
			// Reset all modules load status so they load fresh data from scratch
			Object.keys((store as any)._mutations)
				.filter(mutationName => {
					return mutationName.includes('RESET_LAST_LOAD_DATE')
				})
				.forEach(mutationName => {
					store.commit(mutationName)
				})
		} else {
			// User deauthenticated
			Object.keys((store as any)._mutations)
				.filter(mutationName => {
					return mutationName.includes('ON_DEAUTHENTICATE')
				})
				.forEach(mutationName => {
					store.commit(mutationName)
				})
		}
	}
)

// Watch for plan upgrades
store.watch(
	(state, getters) => getters['subscription/features'],
	() => {
		store.commit('app/SET_UPGRADE_MODAL_REASON', '')
	}
)

export default store
